import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ScrollSmoother from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Only for mouse and trackpad users who have not asked for reduced motion.
// Touch devices keep native scrolling, so sticky elements work as usual there.
const finePointer = window.matchMedia('(pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let headerObserver = null;

// Lets animations.js rebuild its smoother-only triggers. createEvent instead of
// new Event() keeps this working in browsers without the Event constructor.
function notifySmoothScrollChange() {
  const event = document.createEvent('Event');
  event.initEvent('smoothscrollchange', false, false);
  document.dispatchEvent(event);
}

// The page content sits in a fixed wrapper and follows the native scroll position
// with an eased transform. The header can no longer be sticky inside that, so it is
// fixed (see _header.scss) and the content reserves room for it via --header-inner-height.
export function initSmoothScroll() {
  killSmoothScroll();

  const wrapper = document.querySelector('.js-smooth-wrapper');
  const content = document.querySelector('.js-smooth-content');
  if (!wrapper || !content || !finePointer.matches || reducedMotion.matches) {
    notifySmoothScrollChange();
    return null;
  }

  document.documentElement.classList.add('has-smooth-scroll');

  const headerInner = document.querySelector('.header__inner');
  const setHeaderHeight = () => {
    document.documentElement.style.setProperty(
      '--header-inner-height',
      `${headerInner.offsetHeight}px`
    );
  };
  if (headerInner) {
    // Set right away: ScrollTriggers measure positions before a ResizeObserver fires.
    setHeaderHeight();
    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(setHeaderHeight);
      headerObserver.observe(headerInner);
    }
  }

  const smoother = ScrollSmoother.create({
    wrapper,
    content,
    smooth: 1,
  });
  notifySmoothScrollChange();
  return smoother;
}

function killSmoothScroll() {
  const smoother = ScrollSmoother.get();
  if (smoother) smoother.kill();

  if (headerObserver) {
    headerObserver.disconnect();
    headerObserver = null;
  }

  document.documentElement.classList.remove('has-smooth-scroll');
  document.documentElement.style.removeProperty('--header-inner-height');
}

export function isSmoothScrolling() {
  return !!ScrollSmoother.get();
}

// Scroll the page so `element` sits `offset` px below the top of the viewport.
// Through the smoother when it runs: its eased transform means jQuery's offset()
// reports the visual position, not the scroll position.
export function scrollToElement(element, offset = 0) {
  if (!element) return;

  const smoother = ScrollSmoother.get();
  if (smoother) {
    smoother.scrollTo(smoother.offset(element, `top ${offset}px`), true);
    return;
  }

  $('html, body').animate(
    { scrollTop: $(element).offset().top - offset + 'px' },
    750,
    'swing'
  );
}

// Pick the smoother up or drop it when the input device or motion preference changes.
[finePointer, reducedMotion].forEach((query) => {
  const onChange = () => {
    if (!document.querySelector('.js-smooth-wrapper')) return;
    initSmoothScroll();
    ScrollTrigger.refresh();
  };

  if (query.addEventListener) {
    query.addEventListener('change', onChange);
  } else {
    query.addListener(onChange);
  }
});
